import {
  Placement,
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';
import { TmElement, isKeydown } from '@thiememeulenhoff/lit-sdk';
import { html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles, tag } from './tooltip.styles';

@customElement(tag)
export class TooltipComponent extends TmElement {
  readonly #primaryMouseButton = 1; // By default, the left mouse button

  static override styles = styles;

  @property({ reflect: true })
  variant: 'primary' | 'secondary' | 'error' = 'primary';

  @property({ reflect: true })
  placement: Placement = 'right';

  @property()
  offset = '8';

  @property()
  disabled = false;

  @query('#tooltip')
  tooltip!: HTMLElement;

  @query('#arrow')
  arrow!: HTMLElement;

  trigger!: HTMLElement;

  @state()
  hide = false;

  @state()
  open = false;

  timeoutId?: number;

  #destroyObservers?: () => void;

  protected override onCreate = async () => {
    await this.updateComplete;

    this.#setup();
  };

  protected override onDestroy = () => {
    this.#destroyObservers?.();
  };

  protected get triggerElement() {
    return this.trigger;
  }

  override render() {
    return html`
      <slot></slot>

      <div
        popover
        part="tooltip"
        id="tooltip"
        class="tooltip"
        data-test="tooltip"
        hide=${this.hide || nothing}
      >
        <slot name="content"></slot>
        <div id="arrow" class="arrow"></div>
      </div>
    `;
  }

  #clearTimeout() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = undefined;
    }
  }

  #open = (event: Event) => {
    if (this.disabled) {
      return;
    }

    // bitwise operator is required for the buttons property
    if (
      event instanceof MouseEvent &&
      event.buttons & this.#primaryMouseButton // eslint-disable-line no-bitwise
    ) {
      return; // don't show tooltip when mouse is down
    }

    this.#clearTimeout();

    this.#attachTooltip();
    this.open = true;
    this.tooltip.showPopover();
  };

  #close = () => {
    this.open = false;
    this.hide = true;

    const transitionMs =
      parseFloat(window.getComputedStyle(this.tooltip).transitionDuration) *
      1000;

    this.timeoutId = window.setTimeout(() => {
      this.hide = false;
      this.tooltip.hidePopover();
    }, transitionMs);
  };

  #toggle = (event: Event) => {
    if (this.open) this.#close();
    else this.#open(event);
  };

  #handleFocusOut = (event: Event) => {
    if (this && !event.composedPath().includes(this)) this.#close();
  };

  #handleKeydown = (event: Event) => {
    if (isKeydown(event, 'Escape')) this.#close();
  };

  #setup = () => {
    this.#setTrigger();
    this.#setListeners();
    this.#attachTooltip();

    this.#destroyObservers = autoUpdate(
      this.trigger,
      this.tooltip,
      this.#attachTooltip,
    );
  };

  #setListeners() {
    this.listen('keydown', this.#handleKeydown);
    this.listen('focusin', this.#open, this.triggerElement);
    this.listen('mouseenter', this.#open, this.triggerElement);
    this.listen('focusout', this.#handleFocusOut, this.triggerElement);
    this.listen('mouseleave', this.#close, this.triggerElement);
    this.listen('click', this.#toggle, this.trigger);
  }

  #setTrigger = () => {
    const trigger = this.slottedChildren?.[0] as HTMLElement;
    if (!trigger) throw Error('No trigger element found');
    this.trigger = trigger;
  };

  #attachTooltip = () => {
    if (!this.open) return;

    computePosition(this.trigger, this.tooltip, {
      placement: this.placement,
      middleware: [
        shift({ padding: +this.offset }),
        offset(+this.offset),
        arrow({ element: this.arrow }),
        flip(),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign(this.tooltip.style, {
        'inset-inline-start': `${x}px`,
        'inset-block-start': `${y - window.scrollY}px`,
      });
      this.tooltip.style.setProperty('--offset', `${this.offset}px`);

      const arrowX = middlewareData.arrow?.x;
      const arrowY = middlewareData.arrow?.y;
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[placement.split('-')[0]];

      if (staticSide !== undefined) {
        Object.assign(this.arrow.style, {
          left: arrowX != null ? `${arrowX}px` : '',
          top: arrowY != null ? `${arrowY}px` : '',
          right: '',
          bottom: '',
          [staticSide]: '-4px',
        });
      }
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-tooltip': TooltipComponent;
  }
}
